import logoSVG from '../assets/img/logo.svg';
import resetSVG from '../assets/img/reset.svg';
import closeSVG from '../assets/img/close.svg';
import paletteSVG from '../assets/img/palette.svg';
import playCircleSVG from '../assets/img/playCircle.svg';
import speechBubbleSVG from '../assets/img/speechBubble.svg';
import bookOpenSVG from '../assets/img/bookOpen.svg';
import zapSVG from '../assets/img/zap.svg';
import { accessibilityStatementContentHTML, privacyPolicyContentHTML, privacyConsentOverlayContentHTML, privacyWithdrawConsentOverlayContentHTML } from './overlay-content.js';

window.VAE = window.VAE || {};

VAE.UI = (function () {
  'use strict';

  function setPrivacyConsentCookie(status) {
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `vae_privacy_consent=${status}; expires=${expires}; path=/; SameSite=Lax`;
  }

  function getPrivacyConsentStatus() {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith('vae_privacy_consent='))
      ?.split('=')[1];

    if (cookieValue === 'accepted' || cookieValue === 'declined') {
      return cookieValue;
    }
    return 'not_set';
  }

  function injectStyles() {
    if (!document.getElementById('vae-styles')) {
      const link = document.createElement('link');
      link.id = 'vae-styles';
      link.rel = 'stylesheet';
      link.href = '/cdn/vae-ui.css';
      document.head.appendChild(link);
    }

    if (!document.getElementById('vae-shared-styles')) {
      const sharedLink = document.createElement('link');
      sharedLink.id = 'vae-shared-styles';
      sharedLink.rel = 'stylesheet';
      sharedLink.href = '/cdn/vae-ui-shared.css';
      document.head.appendChild(sharedLink);
    }

    if (!document.getElementById('vae-a11y-styles')) {
      const a11yLink = document.createElement('link');
      a11yLink.id = 'vae-a11y-styles';
      a11yLink.rel = 'stylesheet';
      a11yLink.href = '/cdn/vae-a11y.css';
      document.head.appendChild(a11yLink);
    }
    
    // Add styles for fix buttons
    if (!document.getElementById('vae-fix-button-styles')) {
      const fixButtonStyles = document.createElement('style');
      fixButtonStyles.id = 'vae-fix-button-styles';
      fixButtonStyles.textContent = `
        .vae-fix-button {
          background: #007bff;
          color: white;
          border: none;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          margin-left: 8px;
          transition: all 0.2s ease;
        }
        
        .vae-fix-button:hover {
          background: #0056b3;
          transform: translateY(-1px);
        }
        
        .vae-fix-button:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .vae-fix-button.vae-fixing {
          background: #ffc107;
          color: #000;
        }
        
        .vae-fix-button.vae-fixed {
          background: #28a745;
          color: white;
          cursor: default;
        }
        
        .vae-fix-button.vae-fix-error {
          background: #dc3545;
          color: white;
        }
        
        .vae-fix-button.vae-fix-unavailable {
          background: #6c757d;
          color: white;
          cursor: not-allowed;
          opacity: 0.7;
        }
        
        .vae-manual-fix {
          background: #6c757d;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          margin-left: 8px;
        }
        
        .vae-issue-detail-item {
          cursor: pointer;
        }
        
        .vae-detail-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
        }
      `;
      document.head.appendChild(fixButtonStyles);
    }

    // Add styles for status LEDs (analysis completion)
    if (!document.getElementById('vae-led-status-styles')) {
      const ledStatusStyles = document.createElement('style');
      ledStatusStyles.id = 'vae-led-status-styles';
      ledStatusStyles.textContent = `
        /* Button LED positioning and base styles */
        .vae-status-led {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #6b7280; /* Default gray */
          position: absolute;
          top: 8px;
          right: 8px;
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        /* Header LED container positioning */
        .vae-header-logo-container {
          position: relative;
          display: inline-block;
        }

        .vae-header-status-led {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background-color: #6b7280; /* Default gray */
          position: absolute;
          top: -3px;
          right: -3px;
          transition: all 0.3s ease;
          border: 2px solid rgba(255, 255, 255, 0.8);
          box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
          z-index: 10;
        }

        /* Profile active status */
        .vae-status-led.active-profile,
        .vae-header-status-led.active-profile {
          background-color: #8b5cf6; /* Purple */
        }

        /* Settings active status */
        .vae-status-led.active-settings,
        .vae-header-status-led.active-settings {
          background-color: #f59e0b; /* Amber */
        }

        /* Analysis completed status - GREEN (highest priority) */
        .vae-status-led.analysis-completed,
        .vae-header-status-led.analysis-completed {
          background-color: #10b981 !important; /* Green */
          box-shadow: 0 0 8px rgba(16, 185, 129, 0.6) !important;
        }

        /* Analysis in progress status - BLINKING BLUE */
        .vae-status-led.analysis-in-progress,
        .vae-header-status-led.analysis-in-progress {
          background-color: #3b82f6 !important; /* Blue */
          animation: vae-pulse 1.5s infinite;
        }

        @keyframes vae-pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.1);
          }
        }

        /* Data status attributes for accessibility */
        .vae-status-led[data-status="analysis-completed"],
        .vae-header-status-led[data-status="analysis-completed"] {
          background-color: #10b981 !important;
          box-shadow: 0 0 8px rgba(16, 185, 129, 0.6) !important;
        }

      .vae-status-led[data-status="analysis-in-progress"],
      .vae-header-status-led[data-status="analysis-in-progress"] {
        background-color: #3b82f6 !important;
      }

      .vae-status-led[data-status="profile-active"],
      .vae-header-status-led[data-status="profile-active"] {
        background-color: #8b5cf6 !important;
      }

      .vae-status-led[data-status="settings-active"],
      .vae-header-status-led[data-status="settings-active"] {
        background-color: #f59e0b !important;
      }

      .vae-status-led[data-status="inactive"],
      .vae-header-status-led[data-status="inactive"] {
        background-color: #6b7280 !important;
      }

      /* Version number in footer */
      .vae-version-number {
        position: absolute;
        right: 10px;
        bottom: 2px;
        font-size: 10px;
        color: #9ca3af;
        opacity: 0.7;
        font-family: monospace;
        z-index: 10;
      }

      .vae-footer-privacy-row {
        position: relative;
        min-height: 20px;
        padding-right: 50px; /* Space for version number */
      }
      `;
      document.head.appendChild(ledStatusStyles);
    }
  }

  let settingsViewElement = null;
  let overlayElements = {};

  function updatePrivacyConsentOverlayContent(status) {
    const consentOverlay = overlayElements['vae-privacy-consent-overlay'];
    if (!consentOverlay) return;

    const contentContainer = consentOverlay.querySelector('.vae-overlay-content');
    if (!contentContainer) return;

    if (status === 'accepted') {
      contentContainer.innerHTML = privacyWithdrawConsentOverlayContentHTML;
      const withdrawButton = contentContainer.querySelector('#vae-privacy-withdraw-button');
      if (withdrawButton) {
        withdrawButton.onclick = () => { // Use .onclick to ensure only one listener
          setPrivacyConsentCookie('declined'); // Withdrawing means declining further consent
          toggleMenu(); // Close the entire widget
        };
      }
      const keepSettingsButton = contentContainer.querySelector('#vae-privacy-keep-settings-button');
      if (keepSettingsButton) {
        keepSettingsButton.onclick = () => {
          setActiveView('vae-settings-view');
        };
      }
    } else { // 'declined' or 'not_set'
      contentContainer.innerHTML = privacyConsentOverlayContentHTML;
      const acceptButton = contentContainer.querySelector('#vae-privacy-accept-button');
      if (acceptButton) {
        acceptButton.onclick = () => {
          setPrivacyConsentCookie('accepted');
          setActiveView('vae-settings-view');
        };
      }
      const declineButton = contentContainer.querySelector('#vae-privacy-decline-button');
      if (declineButton) {
        declineButton.onclick = () => {
          setPrivacyConsentCookie('declined');
          toggleMenu(); // Close the entire widget
        };
      }
    }
  }

  function setActiveView(viewId) {
    const contentContainer = document.getElementById('vae-content');
    if (!contentContainer) return;

    const allViewElements = [
      settingsViewElement,
      ...Object.values(overlayElements),
    ];

    allViewElements.forEach((el) => {
      if (el && el instanceof HTMLElement) {
        el.style.display = 'none';
      }
    });

    const viewToShow = document.getElementById(viewId);
    if (viewToShow && contentContainer.contains(viewToShow)) {
      viewToShow.style.display = 'block';
    } else if (viewId === 'vae-settings-view' && settingsViewElement) {
      settingsViewElement.style.display = 'block';
    } else if (viewId === 'vae-privacy-consent-overlay' && overlayElements['vae-privacy-consent-overlay']) {
      overlayElements['vae-privacy-consent-overlay'].style.display = 'flex'; // Ensure backdrop works
    } else {
      console.warn(
        `VAE.UI: View with ID "${viewId}" not found or not within #vae-content.`
      );
      if (settingsViewElement) settingsViewElement.style.display = 'block';
    }
  }

  function createWidget() {
    if (document.getElementById('vae-widget-container')) {
      return;
    }

    const container = document.createElement('div');
    container.id = 'vae-widget-container';
    container.setAttribute('role', 'complementary');
    container.setAttribute('aria-label', 'Barrierefreiheits-Einstellungen');

    const button = document.createElement('button');
    button.id = 'vae-button';
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', 'vae-menu');

    button.innerHTML = `
      <div class="vae-button-content">
        <div class="vae-logo-container">
          ${logoSVG}
        </div>
        <div class="vae-status-led"></div>
      </div>
      <div class="vae-mobile-tooltip">
        <div class="vae-mobile-tooltip-content">
          WCAG 2.1 AA Compliance
        </div>
      </div>
    `;

    const menu = document.createElement('div');
    menu.id = 'vae-menu';
    menu.setAttribute('role', 'dialog');
    menu.setAttribute('aria-modal', 'true');
    menu.setAttribute('aria-label', 'Barrierefreiheits-Einstellungen');

    menu.innerHTML = `
      <div class="vae-menu-header">
        <div class="vae-header-content">
          <div class="vae-text-icon-wrapper">
            <div class="vae-header-logo-container">
              ${logoSVG}
              <div class="vae-header-status-led"></div>
            </div>
          </div>
          <div class="vae-menu-title-section">
            <div class="vae-menu-subtitle">automatische</div>
            <h2 class="vae-menu-title">
              Barrierefreiheit
            </h2>
            <div class="vae-menu-attribution">mit <a href="https://accessibleai.eu/" target="_blank" rel="noopener noreferrer">accessibleAI</a></div>
          </div>
          <div class="vae-menu-actions">
            <button
              class="vae-icon-button"
              aria-label="Zurücksetzen"
              title="Zurücksetzen"
              id="vae-reset-button"
            >
              ${resetSVG}
              <span class="vae-button-text">Reset</span>
            </button>
            <button
              class="vae-icon-button"
              aria-label="Schließen"
              id="vae-close-button"
            >
              ${closeSVG}
            </button>
          </div>
        </div>
      </div>

      <div class="vae-content" id="vae-content">
        </div>
    `;

    const footerHTML = `
      <div class="vae-footer-columns-wrapper">
        <div class="vae-footer-links-column vae-footer-single-row">
          <a href="#" class="vae-footer-link" id="vae-accessibility-statement-link">Barrierefreiheitserklärung</a>
          <a href="#" class="vae-footer-link" id="vae-privacy-policy-link">Datenschutzerklärung</a>
        </div>
        <div class="vae-footer-links-column">
          <a href="#" class="vae-footer-link" id="vae-problem-report-link">Problem melden</a>
          <a href="#" class="vae-footer-link" id="vae-optimization-report-link">Analyse</a>
        </div>
      </div>
      <div class="vae-footer-privacy-row">
        <a class="vae-footer-link" id="vae-privacy-settings-link">Privatsphäre-Einstellungen ändern / wiederrufen</a>
      </div>
      <span class="vae-version-number" id="vae-version-display"></span>
    `;

    container.appendChild(button);
    container.appendChild(menu);
    document.body.appendChild(container);

    button.addEventListener('click', toggleMenu);
    const closeButton = document.getElementById('vae-close-button');
    if (closeButton) {
      closeButton.addEventListener('click', toggleMenu);
    }

    const resetButton = document.getElementById('vae-reset-button');
    if (resetButton) {
      resetButton.addEventListener('click', resetAllSettings);
    }

    const logoWrapper = document.querySelector('.vae-text-icon-wrapper');
    if (logoWrapper) {
      logoWrapper.style.cursor = 'pointer';
      logoWrapper.setAttribute('title', 'Analyse öffnen');
      logoWrapper.addEventListener('click', (e) => {
        e.preventDefault();
        setActiveView('vae-auto-fixes');
      });
    }

    const mainContentElement = document.getElementById('vae-content');
    const menuElement = document.getElementById('vae-menu');

    settingsViewElement = document.createElement('div');
    settingsViewElement.id = 'vae-settings-view';
    settingsViewElement.innerHTML = `
        <button class="vae-maximize-button">
          Barrierefreiheit maximieren
        </button>
        <div class="vae-section-divider">
          <h3><span class="vae-section-icon vae-language-icon">${speechBubbleSVG}</span> Sprache</h3>
        </div>
        <div id="vae-language-section-content">
        </div>
        <div class="vae-section-divider">
          <h3><span class="vae-section-icon vae-profile-icon">${zapSVG}</span> Profile</h3>
        </div>
        <div class="vae-profile-section">
          <div id="vae-profile-cards-container">
          </div>
        </div>
        <div class="vae-section-divider">
          <h3><span class="vae-section-icon vae-text-icon">${bookOpenSVG}</span> Texteinstellungen</h3>
        </div>
        <div id="vae-text-section-content">
        </div>
        <div class="vae-section-divider">
          <h3><span class="vae-section-icon vae-colors-icon">${paletteSVG}</span> Farbeinstellungen</h3>
        </div>
        <div id="vae-colors-section-content">
        </div>
        <div class="vae-section-divider">
          <h3><span class="vae-section-icon vae-motion-icon">${playCircleSVG}</span> Bewegungseinstellungen</h3>
        </div>
        <div id="vae-motion-section-content">
        </div>
    `;
    mainContentElement.appendChild(settingsViewElement);

    const defaultOnClose = () => setActiveView('vae-settings-view');

    const autoFixesContentHTML = `
      <p>Diese Einstellungen maximieren automatisch die Barrierefreiheit für alle Benutzer.</p>
      <div id="vae-auto-section-content"></div>`;
    const autoFixesOverlay = VAE.SharedUI.createOverlay({
      id: 'vae-auto-fixes',
      title: 'Barrierefreiheit maximieren',
      contentHTML: autoFixesContentHTML,
      parentElement: mainContentElement,
      onClose: defaultOnClose,
    });
    overlayElements['vae-auto-fixes'] = autoFixesOverlay.overlay;
    autoFixesOverlay.overlay.style.display = 'none';

    const accessibilityStatementOverlay = VAE.SharedUI.createOverlay({
      id: 'vae-accessibility-statement',
      title: 'Barrierefreiheitserklärung',
      contentHTML: accessibilityStatementContentHTML,
      parentElement: mainContentElement,
      onClose: defaultOnClose,
    });
    overlayElements['vae-accessibility-statement'] =
      accessibilityStatementOverlay.overlay;
    accessibilityStatementOverlay.overlay.style.display = 'none';

    const problemReportContentHTML = `
      <p>Haben Sie ein Problem mit der Barrierefreiheit unserer Website gefunden? Bitte teilen Sie uns Ihre Erfahrungen mit, damit wir die Zugänglichkeit verbessern können.</p>
      <form class="vae-form" id="vae-problem-report-form">
        <div class="vae-form-group">
          <label for="vae-form-name" class="vae-form-label">Name</label>
          <input type="text" id="vae-form-name" name="name" class="vae-form-input" placeholder="Ihr Name">
        </div>
        <div class="vae-form-group">
          <label for="vae-form-email" class="vae-form-label">E-Mail</label>
          <input type="email" id="vae-form-email" name="email" class="vae-form-input" placeholder="ihre.email@beispiel.de">
        </div>
        <div class="vae-form-group">
          <label for="vae-form-message" class="vae-form-label">Ihre Nachricht</label>
          <textarea id="vae-form-message" name="message" class="vae-form-textarea" placeholder="Beschreiben Sie das Problem, das Sie festgestellt haben..."></textarea>
        </div>
        <div class="frc-captcha" data-sitekey="${VAE.Config.client.friendlyCaptchaSiteKey}" style="margin-bottom: 1rem;"></div>
        <button type="button" class="vae-form-submit">Absenden</button>
      </form>
    `;
    const problemReportOverlay = VAE.SharedUI.createOverlay({
      id: 'vae-problem-report',
      title: 'Problem melden',
      contentHTML: problemReportContentHTML,
      parentElement: mainContentElement,
      onClose: defaultOnClose,
    });
    overlayElements['vae-problem-report'] = problemReportOverlay.overlay;
    problemReportOverlay.overlay.style.display = 'none';

    const submitButton =
      problemReportOverlay.overlay.querySelector('.vae-form-submit');
    submitButton.addEventListener('click', async function () {
      console.log('VAE.UI: Submit button clicked');
      const form = problemReportOverlay.overlay.querySelector(
        '#vae-problem-report-form'
      );
      const nameInput = form.querySelector('#vae-form-name');
      const emailInput = form.querySelector('#vae-form-email');
      const messageInput = form.querySelector('#vae-form-message');
      const captchaResponseInput = form.querySelector(
        '[name="frc-captcha-solution"]'
      );

      const name = nameInput.value.trim();
      const email = emailInput.value.trim();
      const message = messageInput.value.trim();
      const captchaResponse = captchaResponseInput
        ? captchaResponseInput.value
        : null;

      if (!message) {
        console.log('VAE.UI: Message is empty');
        return;
      }
      if (!captchaResponse) {
        console.log('VAE.UI: Captcha response is empty');
        return;
      }

      submitButton.disabled = true;
      submitButton.textContent = 'Senden...';

      try {
        const reportData = {
          name: name,
          email: email,
          message: message,
          'frc-captcha-solution': captchaResponse,
          url: window.location.href,
        };

        const response = await VAE.API.submitProblemReport(reportData);

        if (response.ok) {
          nameInput.value = '';
          emailInput.value = '';
          messageInput.value = '';
          if (window.friendlyChallenge && form.querySelector('.frc-captcha')) {
            try {
              window.friendlyChallenge.autoWidget.reset();
            } catch (e) {
              console.warn('VAE.UI: Could not reset FriendlyCaptcha widget', e);
            }
          }
          problemReportOverlay.close();
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error(
            'VAE.UI: Problem report submission failed:',
            response.status,
            errorData
          );
        }
      } catch (error) {
        console.error(
          'VAE.UI: Network error during problem report submission:',
          error
        );
      } finally {
        submitButton.disabled = false;
        submitButton.textContent = 'Absenden';
      }
    });

    const privacyPolicyOverlay = VAE.SharedUI.createOverlay({
      id: 'vae-privacy-policy',
      title: 'Datenschutzerklärung',
      contentHTML: privacyPolicyContentHTML,
      parentElement: mainContentElement,
      onClose: defaultOnClose,
    });
    overlayElements['vae-privacy-policy'] = privacyPolicyOverlay.overlay;
    privacyPolicyOverlay.overlay.style.display = 'none';

    const privacyConsentOverlay = VAE.SharedUI.createOverlay({
      id: 'vae-privacy-consent-overlay',
      title: 'Einwilligung zum Datenschutz',
      contentHTML: '', // Content will be set by updatePrivacyConsentOverlayContent
      parentElement: mainContentElement,
      onClose: () => { setActiveView('vae-settings-view'); },
      disableCloseButton: true,
      coverWidget: true
    });
    overlayElements['vae-privacy-consent-overlay'] = privacyConsentOverlay.overlay;
    privacyConsentOverlay.overlay.style.display = 'none'; // Start hidden

    // Set initial content for the privacy overlay
    updatePrivacyConsentOverlayContent(getPrivacyConsentStatus());

    const maximizeButton = settingsViewElement.querySelector(
      '.vae-maximize-button'
    );
    if (maximizeButton) {
      maximizeButton.addEventListener('click', () => {
        setActiveView('vae-auto-fixes');
      });
    }

    const textSectionContainer = settingsViewElement.querySelector(
      '#vae-text-section-content'
    );
    VAE.Text.renderSectionContent(textSectionContainer);

    const colorsSectionContainer = settingsViewElement.querySelector(
      '#vae-colors-section-content'
    );
    VAE.Colors.renderSectionContent(colorsSectionContainer);

    const motionSectionContainer = settingsViewElement.querySelector(
      '#vae-motion-section-content'
    );
    VAE.Motion.renderSectionContent(motionSectionContainer);

    const languageSectionContainer = settingsViewElement.querySelector(
      '#vae-language-section-content'
    );
    VAE.Language.renderSectionContent(languageSectionContainer);

    const profileSectionContainer = settingsViewElement.querySelector(
      '#vae-profile-cards-container'
    );
    VAE.Profiles.renderSectionContent(profileSectionContainer);

    const footerElement = document.createElement('div');
    footerElement.className = 'vae-footer';
    footerElement.innerHTML = footerHTML;
    menuElement.appendChild(footerElement);

    const accessibilityStatementLink = footerElement.querySelector(
      '#vae-accessibility-statement-link'
    );
    if (accessibilityStatementLink) {
      accessibilityStatementLink.addEventListener('click', (e) => {
        e.preventDefault();
        setActiveView('vae-accessibility-statement');
      });
    }

    const problemReportLink = footerElement.querySelector(
      '#vae-problem-report-link'
    );
    if (problemReportLink) {
      problemReportLink.addEventListener('click', (e) => {
        e.preventDefault();
        const friendlyCaptchaUrl =
          'https://cdn.jsdelivr.net/npm/friendly-challenge/widget.min.js';

        if (!window.friendlyChallenge) {
          VAE.loadExternalScript(friendlyCaptchaUrl)
            .then(() => {
              console.log('VAE.UI: Friendly Captcha script loaded.');
              setActiveView('vae-problem-report');
            })
            .catch((error) => {
              console.error(
                'VAE.UI: Failed to load Friendly Captcha script:',
                error
              );
            });
        } else {
          setActiveView('vae-problem-report');
        }
      });
    }

    const privacyPolicyLink = footerElement.querySelector(
      '#vae-privacy-policy-link'
    );
    if (privacyPolicyLink) {
      privacyPolicyLink.addEventListener('click', (e) => {
        e.preventDefault();
        setActiveView('vae-privacy-policy');
      });
    }

    const optimizationReportLink = footerElement.querySelector(
      '#vae-optimization-report-link'
    );
    if (optimizationReportLink) {
      optimizationReportLink.addEventListener('click', (e) => {
        e.preventDefault();
        setActiveView('vae-auto-fixes');
      });
    }

    const privacySettingsLink = footerElement.querySelector(
      '#vae-privacy-settings-link'
    );
    if (privacySettingsLink) {
      privacySettingsLink.addEventListener('click', (e) => {
        e.preventDefault();
        updatePrivacyConsentOverlayContent(getPrivacyConsentStatus());
        setActiveView('vae-privacy-consent-overlay');
      });
    }

    setupStateSubscriptions();
    updateUIFromState();
    setActiveView('vae-settings-view');
    
    // Listen for update notifications
    VAE.events.on('update-available', showUpdateNotification);
    
    // Listen for version changes
    VAE.events.on('version-updated', updateVersionDisplay);
    
    // Update version display after footer is created
    setTimeout(() => {
      updateVersionDisplay();
    }, 100);

    // Consistency Test Button Event Listener
    setTimeout(() => {
      const consistencyTestButton = document.getElementById('vae-consistency-test-button');
      if (consistencyTestButton) {
        consistencyTestButton.addEventListener('click', async () => {
          if (VAE.ConsistencyTester.isRunning()) {
            alert('Ein Konsistenz-Test läuft bereits. Bitte warten Sie, bis er abgeschlossen ist.');
            return;
          }

          const confirmed = confirm(
            'Der Konsistenz-Test führt 10 identische Analysen durch und kann mehrere Minuten dauern. ' +
            'Während des Tests sollten Sie die Seite nicht verlassen oder neu laden. ' +
            'Möchten Sie fortfahren?'
          );

          if (confirmed) {
            try {
              consistencyTestButton.disabled = true;
              consistencyTestButton.textContent = 'Test läuft...';
              
              console.log('VAE.UI: Starting consistency test...');
              const results = await VAE.ConsistencyTester.runConsistencyTest(10);
              
              console.log('VAE.UI: Consistency test completed:', results);
              alert('Konsistenz-Test abgeschlossen! Die Ergebnisse wurden in einem neuen Fenster geöffnet.');
              
            } catch (error) {
              console.error('VAE.UI: Consistency test failed:', error);
              alert('Fehler beim Konsistenz-Test: ' + error.message);
            } finally {
              consistencyTestButton.disabled = false;
              consistencyTestButton.textContent = 'Konsistenz-Test starten (10 Analysen)';
            }
          }
        });
      }

      // Regenerate Report Button Event Listener
      const regenerateReportButton = document.getElementById('vae-regenerate-report-button');
      if (regenerateReportButton) {
        regenerateReportButton.addEventListener('click', () => {
          try {
            console.log('VAE.UI: Regenerating enhanced report...');
            const result = VAE.ConsistencyTester.regenerateReport();
            
            if (result) {
              console.log('VAE.UI: Enhanced report regenerated successfully');
              alert('Erweiterter Report wurde erfolgreich generiert und in einem neuen Fenster geöffnet!');
            }
            
          } catch (error) {
            console.error('VAE.UI: Report regeneration failed:', error);
            alert('Fehler beim Generieren des Reports: ' + error.message);
          }
        });
      }

      // Import Results Button Event Listener
      const importResultsButton = document.getElementById('vae-import-results-button');
      if (importResultsButton) {
        importResultsButton.addEventListener('click', () => {
          try {
            console.log('VAE.UI: Starting import process...');
            VAE.ConsistencyTester.importResults();
            
          } catch (error) {
            console.error('VAE.UI: Import failed:', error);
            alert('Fehler beim Importieren: ' + error.message);
          }
        });
      }

      // Debug Single Test Button Event Listener
      const debugSingleTestButton = document.getElementById('vae-debug-single-test-button');
      if (debugSingleTestButton) {
        debugSingleTestButton.addEventListener('click', async () => {
          if (VAE.ConsistencyTester.isRunning()) {
            alert('Ein Test läuft bereits. Bitte warten Sie, bis er abgeschlossen ist.');
            return;
          }

          const confirmed = confirm(
            'Debug-Test führt eine einzelne Iteration mit detailliertem Logging durch. ' +
            'Öffnen Sie die Browser-Konsole, um die Debug-Ausgaben zu sehen. ' +
            'Möchten Sie fortfahren?'
          );

          if (confirmed) {
            try {
              debugSingleTestButton.disabled = true;
              debugSingleTestButton.textContent = 'Debug-Test läuft...';
              
              console.log('VAE.UI: Starting debug single test...');
              const results = await VAE.ConsistencyTester.runConsistencyTest(1);
              
              console.log('VAE.UI: Debug test completed:', results);
              alert('Debug-Test abgeschlossen! Prüfen Sie die Browser-Konsole für detaillierte Logs.');
              
            } catch (error) {
              console.error('VAE.UI: Debug test failed:', error);
              alert('Fehler beim Debug-Test: ' + error.message);
            } finally {
              debugSingleTestButton.disabled = false;
              debugSingleTestButton.textContent = 'Debug: Einzelne Iteration testen';
            }
          }
        });
      }
    }, 100);
  }

  function setupStateSubscriptions() {
    VAE.StateManager.subscribe('*', updateGlobalUIState);
    VAE.StateManager.subscribe('colors', updateAnalysisButtonStyling);

    const statusLed = document.querySelector('.vae-status-led');
    if (statusLed) {
      VAE.StateManager.subscribe('*', function (globalState) {
        // Merge analysis state with global state
        const mergedState = {
          ...globalState,
          ...(VAE.UI._analysisState || {})
        };
        updateStatusLed(statusLed, mergedState);
      });
      // Initial update with merged state
      const initialState = {
        ...VAE.StateManager.getState(),
        ...(VAE.UI._analysisState || {})
      };
      updateStatusLed(statusLed, initialState);
    }

    const headerStatusLed = document.querySelector('.vae-header-status-led');
    if (headerStatusLed) {
      VAE.StateManager.subscribe('*', function (globalState) {
        // Merge analysis state with global state
        const mergedState = {
          ...globalState,
          ...(VAE.UI._analysisState || {})
        };
        updateStatusLed(headerStatusLed, mergedState);
      });
      // Initial update with merged state
      const initialState = {
        ...VAE.StateManager.getState(),
        ...(VAE.UI._analysisState || {})
      };
      updateStatusLed(headerStatusLed, initialState);
    }
  }

  function updateGlobalUIState(state) {
    const globalComplianceStatus = assessGlobalCompliance(state);

    const button = document.getElementById('vae-button');
    if (button) {
      button.setAttribute('data-compliance', globalComplianceStatus);
    }
  }

  function assessGlobalCompliance(state) {
    const profileActive = state.profile !== null && state.profile !== 'custom';
    const settingsActive = ['text', 'colors', 'motion'].some((section) =>
      hasActiveSettings(state[section])
    );

    if (profileActive) {
      return 'high';
    } else if (settingsActive) {
      return 'partial';
    } else {
      return 'none';
    }
  }

  function hasActiveSettings(sectionState) {
    if (!sectionState) return false;
    return Object.entries(sectionState).some(([key, value]) => {
      const defaults = {
        fontSize: 1,
        lineHeight: 1,
        letterSpacing: 1,
        wordSpacing: 1,
        fontFamily: 'default',
        textAlign: 'left',
        highlightLinks: false,
        highlightHeadings: false,
        dyslexiaFont: false,
        contrast: 'normal',
        colorScheme: 'default',
        saturation: 1,
        backgroundColor: '#ffffff',
        textColor: '#000000',
        linkColor: '#0000ee',
        highlightColor: '#ffff00',
        darkMode: false,
        highContrast: false,
        invertColors: false,
        colorBlindMode: 'none',
        reducedMotion: false,
        animationSpeed: 1,
        autoplayVideos: true,
        disableAnimations: false,
        smoothScrolling: true,
        cursorSize: 1,
        cursorContrast: false,
        focusIndicator: false,
      };
      return key in defaults && value !== defaults[key];
    });
  }

  function updateStatusLed(ledElement, state) {
    const profileActive = state.profile !== null && state.profile !== 'custom';
    const settingsActive = ['text', 'colors', 'motion'].some((section) =>
      hasActiveSettings(state[section])
    );
    
    // Check for analysis completion status
    const analysisCompleted = state.analysisCompleted === true;
    const analysisInProgress = state.analysisInProgress === true;
    
    console.log('VAE.UI: Updating LED status', {
      element: ledElement.className,
      analysisCompleted,
      analysisInProgress,
      profileActive,
      settingsActive
    });

    // Clear all status classes
    ledElement.classList.remove('active-profile', 'active-settings', 'analysis-completed', 'analysis-in-progress');

    // Set status based on priority: Analysis > Profile > Settings > Inactive
    if (analysisCompleted) {
      ledElement.classList.add('analysis-completed');
      ledElement.setAttribute('data-status', 'analysis-completed');
      console.log('VAE.UI: LED set to analysis-completed (green)');
    } else if (analysisInProgress) {
      ledElement.classList.add('analysis-in-progress');
      ledElement.setAttribute('data-status', 'analysis-in-progress');
    } else if (profileActive) {
      ledElement.classList.add('active-profile');
      ledElement.setAttribute('data-status', 'profile-active');
    } else if (settingsActive) {
      ledElement.classList.add('active-settings');
      ledElement.setAttribute('data-status', 'settings-active');
    } else {
      ledElement.setAttribute('data-status', 'inactive');
    }
  }

  function updateUIFromState() {
    const currentState = VAE.StateManager.getState();
    updateGlobalUIState(currentState);

    // Update button LED
    const statusLed = document.querySelector('.vae-status-led');
    if (statusLed) {
      updateStatusLed(statusLed, currentState);
    }

    // Update header LED
    const headerStatusLed = document.querySelector('.vae-header-status-led');
    if (headerStatusLed) {
      updateStatusLed(headerStatusLed, currentState);
    }

    const menu = document.getElementById('vae-menu');
    const button = document.getElementById('vae-button');

    // Auto-open disabled - widget should only open on user interaction
    // if (
    //   menu &&
    //   button &&
    //   VAE.Config?.settings?.isOpen === true &&
    //   !menu.classList.contains('open')
    // ) {
    //   toggleMenu();
    // }
  }

  function resetAllSettings() {
    VAE.StateManager.resetAll();
    setActiveView('vae-settings-view');
  }

  function toggleMenu() {
    const menu = document.getElementById('vae-menu');
    const button = document.getElementById('vae-button');

    if (!menu || !button) {
      console.error('VAE.UI: Menu or Button element not found!');
      return;
    }

    const isOpen = menu.classList.contains('open');

    if (!isOpen) {
      menu.classList.add('open');
      button.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', handleClickOutside);
      // Privacy consent layer disabled - skip consent check
      // const consentStatus = getPrivacyConsentStatus();
      // if (consentStatus === 'not_set') {
      //   updatePrivacyConsentOverlayContent('not_set');
      //   setActiveView('vae-privacy-consent-overlay');
      // }
    } else {
      menu.classList.remove('open');
      button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', handleClickOutside);
      setActiveView('vae-settings-view');
    }

    if (VAE.Config && VAE.Config.settings) {
      VAE.Config.settings.isOpen = !isOpen;
    }
  }

  function handleClickOutside(event) {
    const container = document.getElementById('vae-widget-container');
    const menu = document.getElementById('vae-menu');

    if (
      menu &&
      menu.classList.contains('open') &&
      container &&
      !container.contains(event.target) &&
      (!overlayElements['vae-privacy-consent-overlay'] || overlayElements['vae-privacy-consent-overlay'].style.display === 'none')
    ) {
      const button = document.getElementById('vae-button');
      if (!button || !button.contains(event.target)) {
        toggleMenu();
      }
    }
  }

  function updateAnalysisButtonStyling(colorState) {
    const startBtn = document.querySelector('.vae-primary-btn');
    if (!startBtn) return;

    // Reset to default styling first
    startBtn.style.background = '#2563eb';
    startBtn.style.color = 'white';
    startBtn.style.border = 'none';

    // Apply contrast-specific styling
    if (colorState?.highContrast) {
      startBtn.style.background = '#000000';
      startBtn.style.color = '#ffffff';
      startBtn.style.border = '2px solid #ffffff';
    } else if (colorState?.highContrastLight) {
      startBtn.style.background = '#ffffff';
      startBtn.style.color = '#000000';
      startBtn.style.border = '2px solid #000000';
    }
  }



  // New function to update analysis status
  function setAnalysisStatus(status) {
    console.log('VAE.UI: Setting analysis status:', status);
    
    // Store analysis status in UI module directly
    if (!VAE.UI._analysisState) {
      VAE.UI._analysisState = {
        analysisCompleted: false,
        analysisInProgress: false
      };
    }
    
    // Update analysis state
    if (status === 'completed') {
      VAE.UI._analysisState.analysisCompleted = true;
      VAE.UI._analysisState.analysisInProgress = false;
    } else if (status === 'in-progress') {
      VAE.UI._analysisState.analysisCompleted = false;
      VAE.UI._analysisState.analysisInProgress = true;
    } else if (status === 'idle') {
      VAE.UI._analysisState.analysisCompleted = false;
      VAE.UI._analysisState.analysisInProgress = false;
    }
    
    // Get current state from StateManager and merge with analysis state
    const currentState = VAE.StateManager.getState();
    const mergedState = {
      ...currentState,
      ...VAE.UI._analysisState
    };
    
    // Update both LEDs immediately
    const statusLed = document.querySelector('.vae-status-led');
    const headerStatusLed = document.querySelector('.vae-header-status-led');
    
    if (statusLed) {
      updateStatusLed(statusLed, mergedState);
    } else {
      console.log('VAE.UI: Button LED not found');
    }
    
    if (headerStatusLed) {
      updateStatusLed(headerStatusLed, mergedState);
    } else {
      console.log('VAE.UI: Header LED not found');
    }
    
    // Trigger state manager subscribers to update LEDs
    if (VAE.StateManager && VAE.StateManager.notifySubscribers) {
      VAE.StateManager.notifySubscribers('global');
    }
  }

  // Update version display in footer
  function updateVersionDisplay() {
    const versionElement = document.getElementById('vae-version-display');
    if (versionElement && window.VAE && window.VAE.VersionManager) {
      const currentVersion = VAE.VersionManager.getCurrentVersion();
      versionElement.textContent = `v${currentVersion}`;
      console.log('VAE.UI: Version display updated:', currentVersion);
    } else {
      console.log('VAE.UI: Version display not updated - element or VersionManager not available', {
        element: !!versionElement,
        VAE: !!window.VAE,
        VersionManager: !!(window.VAE && window.VAE.VersionManager)
      });
    }
  }

  // Show update notification
  function showUpdateNotification(updateInfo) {
    console.log('VAE.UI: Update available notification', updateInfo);
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'vae-update-notification';
    notification.innerHTML = `
      <div class="vae-update-icon">⚡</div>
      <div class="vae-update-content">
        <div class="vae-update-title">Widget-Update verfügbar!</div>
        <div class="vae-update-version">Version ${updateInfo.newVersion}</div>
        ${updateInfo.critical ? '<div class="vae-update-critical">Kritisches Update - Automatische Aktualisierung in 30 Sekunden</div>' : ''}
      </div>
      <button class="vae-update-button" onclick="VAE.VersionManager.reloadWidget()">Jetzt aktualisieren</button>
      <button class="vae-update-close" onclick="this.parentElement.remove()">×</button>
    `;
    
    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .vae-update-notification {
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ffffff;
        border: 2px solid #3b82f6;
        border-radius: 12px;
        padding: 16px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        z-index: 100001;
        display: flex;
        align-items: center;
        gap: 12px;
        max-width: 400px;
        animation: vae-slide-in 0.3s ease-out;
      }
      
      @keyframes vae-slide-in {
        from {
          transform: translateX(120%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      .vae-update-icon {
        font-size: 24px;
        background: linear-gradient(45deg, #3b82f6, #60a5fa);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
      
      .vae-update-content {
        flex: 1;
      }
      
      .vae-update-title {
        font-weight: 600;
        color: #1f2937;
        margin-bottom: 4px;
      }
      
      .vae-update-version {
        font-size: 14px;
        color: #6b7280;
      }
      
      .vae-update-critical {
        font-size: 12px;
        color: #ef4444;
        margin-top: 4px;
        font-weight: 500;
      }
      
      .vae-update-button {
        background: #3b82f6;
        color: white;
        border: none;
        border-radius: 6px;
        padding: 8px 16px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }
      
      .vae-update-button:hover {
        background: #2563eb;
      }
      
      .vae-update-close {
        position: absolute;
        top: 8px;
        right: 8px;
        background: none;
        border: none;
        font-size: 20px;
        color: #6b7280;
        cursor: pointer;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: all 0.2s;
      }
      
      .vae-update-close:hover {
        background: #f3f4f6;
        color: #1f2937;
      }
      
      @media (max-width: 640px) {
        .vae-update-notification {
          top: 10px;
          right: 10px;
          left: 10px;
          max-width: none;
        }
      }
    `;
    
    // Add to document if not already added
    if (!document.querySelector('style[data-vae-update-styles]')) {
      style.setAttribute('data-vae-update-styles', 'true');
      document.head.appendChild(style);
    }
    
    document.body.appendChild(notification);
    
    // Auto-remove after 30 seconds if not critical
    if (!updateInfo.critical) {
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 30000);
    }
  }

  // Cleanup function for widget removal
  function destroy() {
    const container = document.getElementById('vae-widget-container');
    if (container) {
      container.remove();
    }
    
    // Remove event listeners
    VAE.events.off('update-available', showUpdateNotification);
    
    // Clean up any remaining notification
    const notification = document.querySelector('.vae-update-notification');
    if (notification) {
      notification.remove();
    }
  }

  // Open widget programmatically
  function open() {
    const menu = document.getElementById('vae-menu');
    const button = document.getElementById('vae-button');
    
    if (menu && button && !menu.classList.contains('open')) {
      toggleMenu();
    }
  }

  return {
    injectStyles,
    createWidget,
    toggleMenu,
    setAnalysisStatus,
    destroy,
    open,
  };
})();
